import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AppToasterService } from '@shared/service/app-toaster.service';
import { CookieService } from 'ngx-cookie-service';
import { HttpRequest, HttpHandler, HttpEvent, HttpResponse } from '@angular/common/http';
import { AccountService, AlertService, AppTenantService } from '@app/_services';
import { SSOAuthService } from '@app/_services/sso.service';
import { Platform } from '@ionic/angular';
import { AppConsts } from '../../../src/shared/AppConsts';
import { mergeMap } from 'rxjs/operators';
import { CookieAuthService } from '@app/_services/cookie-auth.service';

// These are few APIs where Cookies are updated through 'Set-Cookie' header
const COOKIE_UPDATE_APIS = [
    "TokenAuth/AuthenticateUser",
    "TokenAuth/SwitchTenant",
    "account/cleartoken",
    "cams-service/token/sso",
    "cams-service/token/authenticate",
    "cams-service/token/sso/refresh",
];

// Error codes that represent SSL issues, according the 'cordova-plugin-sslsupport' docs
const SSL_ERROR_CODES = [-1022, -1200, -1201, -1202, -1203, -1204, -1205, -1206];

/**
 *
 */
@Injectable()
export class SslHttpClient {

    constructor(
        private accountService: AccountService, 
        private _appTenantService: AppTenantService, 
        private alertService: AlertService,
        private _notify: AppToasterService,
        private ssoAuthService: SSOAuthService,
        private platform: Platform,
        private _cookieService: CookieService,
        public _cookieAuthService: CookieAuthService) {
    }

    request(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>>{
        return new Observable((subscriber) => {
            this.pluginAPICall(request, next).then((pluginRes) => {
                const cookieUpdateAPI = COOKIE_UPDATE_APIS.find((api) => request.url.endsWith(api));

                if (cookieUpdateAPI) {
                    this.SyncCookies();
                }
                subscriber.next(pluginRes);
                subscriber.complete();
            }).catch((error) => {
                // Check if it is SSL error, if yes than show error message, else redirect to use httpClient instead.
                if (!SSL_ERROR_CODES.includes(error.errorcode)) {
                    subscriber.next({ sslFailed: true, authToken: error.authToken });
                    subscriber.complete();
                    return;
                }
                if (error.httperrorcode === 401) {
                    // In some cases we get 401 on logout API also, causing infinite API call loop, this condition is to avoid that
                    if (!request.url.includes('account/logout')) {
                        this.accountService.logout(true);
                    } else {
                        this.ssoAuthService.logout();
                    }          
                }
                if (error.httperrorcode === 429) {
                    this._notify.error(error.error, null, { preventDuplicates: true, positionClass: 'toast-top-right' });
                    subscriber.error(error);
                }

                this._notify.error("Can not establish secure connection with the server!", null, { preventDuplicates: true, positionClass: 'toast-top-right' });
                subscriber.error(error);
            });
        }).pipe(mergeMap((result: HttpResponse<any>) => {
            return new Observable<HttpEvent<any>>((subs) => {
                subs.next(result);
                subs.complete();
            });
        }));
    }

    /**
     * This logic is put into a separate promise function because having it directly in observable was causing
     * UI freeze (data not reflecting till we touch the screen), having it here fix the issue for some reason
     * @returns a Promise of type HttpResponse
     */
    pluginAPICall(req: HttpRequest<any>, next: HttpHandler): Promise<HttpResponse<any>> {
        return new Promise<HttpResponse<any>>(async (resolve, reject) => {
            const authToken = await this._cookieAuthService.getCookie(AppConsts.authorization.AuthToken);
            // A value of null (NSNull) crashed on iOS, so we're giving it 'application/json' in that case to avoid it
            const contentType = req.headers.get("Content-Type") ? req.headers.get("Content-Type") : 'application/json';
            const reqHeaders = {
                "Accept": req.headers.get("Accept"),
                "Content-Type": contentType,
                "responseType": req.responseType,
                Authorization: 'Bearer ' + authToken
            };

            const params = { url : req.urlWithParams, data: req.body, headers: reqHeaders };
            console.warn('HTTP request:', params);
            if (req.method.toLowerCase() === 'get') {
                window['sslHTTP'].get(params, onSslHttpSuccess, onSslHttpFailure)
            } else if (req.method.toLowerCase() === 'put') {
                window['sslHTTP'].put(params, onSslHttpSuccess, onSslHttpFailure)
            } else if (req.method.toLowerCase() === 'post') {
                window['sslHTTP'].post(params, onSslHttpSuccess, onSslHttpFailure)
            } else if (req.method.toLowerCase() === 'delete') {
                window['sslHTTP'].delete(params, onSslHttpSuccess, onSslHttpFailure)
            }

            function onSslHttpSuccess(response) {
                const res = new HttpResponse();
                let result = response.data;
                try {
                    result = JSON.parse(response.data);
                } catch (error) {
                    console.error('onSslHttpSuccess parsing failed', error);
                }
                console.warn('HTTP response:', req.urlWithParams, response);
                let updatedRes = res.clone({ body: result.result ? result.result : result });
                resolve(updatedRes);
            }

            function onSslHttpFailure(error) {
                abp.ui.clearBusy()
                console.error('HTTP response:', req.urlWithParams, JSON.stringify(error));
                // As in case of failure we need to direct the call to use httpClient, and on iOS we can't access Cookies
                // So need to instead send "Authorization" in the header, direct async call is not allowed where it's finally used,
                // so we're sending it like this instead
                error.authToken = authToken;
                reject(error);
            }
        })
    }

    /**
     * As we're using SslPlugin for API calls in case of mobile, it handles Cookies natively, inaccessible to javascript
     * so here, in case of some particular APIs, we're getting those cookies and setting it in javascript side
     * to be able to access them manually as 'Authorization' header for some APIs or automatically as header Cookies
     * in case we interchangeably use Angular httpClient for some APIs.
     */
    SyncCookies() {
        const thisRef = this;
        window['sslHTTP'].getCookies('wakandi.com', function(cookies) {
            const authTokenCookie = cookies[AppConsts.authorization.AuthToken]
            const encryptedAuthTokenName = cookies[AppConsts.authorization.encryptedAuthTokenName]
            const wakToken = cookies[AppConsts.SSO.token]
            const wakRefreshToken = cookies[AppConsts.SSO.refreshToken]

            const expiryDate = new Date(new Date().getTime() + 86400000); // 1 Day
            if (authTokenCookie) {
                thisRef._cookieService.set(authTokenCookie.name, authTokenCookie.value, expiryDate, authTokenCookie.path, 'wakandi.com');
                /* abp.utils.setCookieValue(
                    authTokenCookie.name,
                    authTokenCookie.value,
                    expiryDate,
                    authTokenCookie.path
                );  */
            }
            if (encryptedAuthTokenName) {
                thisRef._cookieService.set(encryptedAuthTokenName.name, encryptedAuthTokenName.value, expiryDate, encryptedAuthTokenName.path, 'wakandi.com');
                /* abp.utils.setCookieValue(
                    encryptedAuthTokenName.name,
                    encryptedAuthTokenName.value,
                    expiryDate,
                    encryptedAuthTokenName.path
                );  */
            }
            if (wakToken) {
                thisRef._cookieService.set(wakToken.name, wakToken.value, expiryDate, wakToken.path, 'wakandi.com');
                /* abp.utils.setCookieValue(
                    wakToken.name,
                    wakToken.value,
                    expiryDate,
                    wakToken.path
                ); */
            }
            if (wakRefreshToken) {
                thisRef._cookieService.set(wakRefreshToken.name, wakRefreshToken.value, expiryDate, wakRefreshToken.path, 'wakandi.com');
                /* abp.utils.setCookieValue(
                    wakRefreshToken.name,
                    wakRefreshToken.value,
                    expiryDate,
                    wakRefreshToken.path
                ); */ 
            }
        }, function() {
            console.error('SyncCookies error :(');
        });
    }
}